import classes from './_form_item.module.scss';

/** @typedef {import('./types.js').ValidationCollection} ValidationCollection */
/** @typedef {import('./types.js').ValidationResultCollection} ValidationResultCollection */

/**
 * Wraps a form element or MusicTextFieldV3 to provide validation checking.
 *
 * @class
 *
 * @property {HTMLInputElement} input
 * @property {Boolean} required
 * @property {Boolean} mustContainNumber
 * @property {Boolean} mustContainNumberAndLetter
 * @property {Boolean} userNameUnique
 * @property {Boolean} emailUnique
 * @property {Boolean} passwordsMustMatch
 * @property {undefined|Number} minlength
 */
export default class UaFormItem extends HTMLElement {
  /** @type HTMLInputElement */
  input;
  /** @type HTMLDivElement */
  #validationBlock;
  /** @type {ValidationCollection | undefined} */
  #validations;
  /** @type {ValidationResultCollection | {}} */
  #validationResults = {};

  /**
   * If set to `true`, validate field every time `isValid()` is called on it.
   * This behavior is useful when the field validation depends on the value of other fields.
   * For example (so far, the only example), the user may put the user-password-confirmation
   * field into an invalid state by changing the value of the user-password field.
   *
   * To set this value to `true`, include `data-always-validate` as an attribute on the form item.
   */
  /** @type Boolean */
  #alwaysValidate = false;

  /**
   * @param {string[]} list - Array of validation rule types.
   * @param {ValidationCollection} validations - POJO of validations.
   * @param {boolean} multipleRules - Whether to apply multiple rules to the input.
   */
  addValidations(list, validations, multipleRules = true) {
    this.#validations = validations;
    this.#alwaysValidate = this.input.dataset['alwaysValidate'];

    /**
     * To determine what validation rules should be applied to the input element,
     * we check the element's dataset keys and its HTML attribute names for strings that match
     * the names of the validation rules.
     * 
     * multipleRules set to false means that the input will only display the 'data-*' attributes that are present in the validation rules.
     * Attributes like 'required' are ommited and not used to determine which validations to display.
     * For example, if an input has a rule attribute and a required attribute,
     * you may not want to display the generic required validation message and only display the rule validation message.
     * 
     * If multipleRules is true, all attributes are used to determine which validations to display.
     * This is the default behavior.
     */
    const dataAttributes = Object.keys(this.input.dataset);
    const htmlAttributes = multipleRules ? this.input.getAttributeNames().filter(item => !item.match(/^data-/)) : [];
    const dataAndHtmlAttributes = [...dataAttributes, ...htmlAttributes];

    // Grab attributes that match keys in the validation rules
    this.inputRules = dataAndHtmlAttributes.filter((attr) => {
      return list.includes(attr)
    });

    /**
     * Add attributes and sibling elements as needed for each of the validation
     * rules that are to be applied.
     */
    this.inputRules.forEach((rule) => {
      // Add a validation-message element.
      this.addValidationMessageElements(rule, this.input);

      /**
       * The rule may include a regular-expression string value assigned to a `pattern` key.
       * If so, the rule's `isValid` callback leverages the native `pattern` attribute to
       * validate the input value. To enable this native validation, we assign the regex string
       * to `pattern` on the input element.
       */
      this.addPattern(rule, this.input);
    });
  }

  /**
   * Returns whether or not there are validation handlers.
   * @return {boolean}
   */
  hasValidations() {
    return !!this.#validations;
  }

  /**
   * Returns overall validity of the form item.
   * If there are no validation handlers, return true.
   * If any validations fail, return false.
   */
  get isValid() {
    if (!this.hasValidations()) {
      // If there are no validations, the input is valid.
      return true;
    } else if (this.#alwaysValidate || this.validationResultValues().length === 0) {
      /**
       * If there is validation handling and
       *
       * - the form item is configured to run the validations for every `isValid` call, OR
       * - the validation results have not yet been populated,
       *
       * run the validations.
       */
      this.validate();
    }

    // Check for overall validity by looping over rules, and determining if any failed.
    return !this.inputRules.map((rule) => this.#validationResults[rule].isValid).includes(false);
  }

  get hasRequiredValidation() {
    return Object.values(this.inputRules).find(inputRule => inputRule === 'required') !== null;
  }

  /**
   * Get all results for validations on this form item.
   * @returns {ValidationResult[]} array of validation results
   */
  validationResultValues() {
    return Object.values(this.#validationResults);
  }

  /**
   * Run all validation handlers for the rules that apply to this form item.
   * Update the validity state for each associated ua-validation-message element.
   */
  validate() {
    if (!this.hasValidations() || !this.inputRules?.length) {
      return;
    }

    this.inputRules.forEach((rule) => {
      this.#validationResults[rule] = {
        isValid: this.#validations[rule].isValid(this.input),
      }
    });

    Promise.all(
      this.validationResultValues().map((validationResult) => validationResult.isValid)
    ).then((results) => {
      /* Once all validation callbacks are resolved,
       * Update the validity on the messages, which has a side effect of
       * updating the ua-validation-message templates.
       */
      const hasInputError = results.includes(false);
      if (hasInputError) {
        this.input.setAttribute('aria-invalid', 'true');
      } else {
        this.input.removeAttribute('aria-invalid');
      }

      const messageElms = this.validationMessageElements();
      const firstSuccessfulResultIndex = results.findIndex((result) => result === true);
      messageElms.forEach((elm, index) => {
        const announceSuccessProgress = index === firstSuccessfulResultIndex;
        this.setMessageElmValidity(elm, results, announceSuccessProgress);
      });

    });
  }

  /**
   * Set validation message for neutral state.
   */
  setNeutralMessage() {
    const messageElms = this.validationMessageElements();
    messageElms.forEach((elm) => {
      elm.message = this.#validations[elm.validationType].message(this.input);
      elm.state = 'neutral';
    });
  }

  /**
   * @param {UaValidationMessage} elm
   * Sets the element's validity and its message.
   */
  async setMessageElmValidity(elm, results = [], announceSuccessProgress = false) {
    const isValid = await this.#validationResults[elm.validationType].isValid;
    elm.completedRequirementsCount = results.filter(result => result === true).length;
    elm.totalRequirementsCount = results.length;
    elm.announceSuccessProgress = announceSuccessProgress;
    elm.message = this.#validations[elm.validationType].message(this.input, isValid);
    elm.state = isValid ? 'success' : 'error';
  }

  /**
   * @returns {UaValidationMessage[]} all UaValidationMessages in the validation block
   */
  validationMessageElements() {
    return this.#validationBlock.querySelectorAll('ua-validation-message');
  }

  /**
   * Create a UaValidationMessage element of the appropriate type and append it to
   * the validations block for this form item. If the value is not blank, run the validations.
   *
   * @param {string} type - The validation type to assign to this element.
   * @param {HTMLElement} input - The input element for this form item.
   */
  addValidationMessageElements(type, input) {
    const className = `js-${input.id}-check`;
    const msg = document.createElement('ua-validation-message');
    msg.classList.add(className);
    msg.fieldName = input.id;
    msg.validationType =  type;
    msg.validationState = 'neutral'; // default value
    this.#validationBlock.appendChild(msg);

    if (this.#validations[type].allowNeutral) {
     this.setNeutralMessage();
    }

    if (input.value) {
      this.validate();
    }
  }

  /**
   * If the validation rule includes a `pattern` property, set it as an attribute on the input
   * element to enable native regex validation.
   *
   * @param {String} rule - name of a rule in the validation-rule collection
   * @param {HTMLInputElement} input - the form item's input element
   */
  addPattern(rule, input) {
    // Get the value of the validation rule's `pattern` property.
    const pattern = this.#validations[rule].pattern;

    /** If the rule doesn't include a pattern, there is nothing more to do. */
    if (!pattern) { return }

    /**
     * If the input already has a value for `pattern`, fail loudly: we will need to sort out the
     * conflicting regex validations.
     */
    if (input.getAttribute('pattern')) {
      throw new Error('Pattern attribute already exists on element');
    }

    input.setAttribute('pattern', pattern);
  }

  /**
   * Create a MusicRequiredV3 element and append it to the validations block for this form item.
   */
  addRequiredAnnotation() {
    const requiredElm = document.createElement('music-required-v3');
    requiredElm.classList.add(classes['ua-form-item__required']);
    requiredElm.setAttribute('aria-hidden', 'true');
    this.#validationBlock.appendChild(requiredElm);
  }

  /**
   * Set up validations to run on this form item whenever the user types into it or changes focus
   * to another element.
   */
  setupEventListeners() {
    this.input.addEventListener('blur', this.validate.bind(this));
    this.input.addEventListener('input', this.validate.bind(this));
  }

  connectedCallback() {
    this.#validationResults = {};
    let inputs = this.querySelectorAll('input, textarea, select, sl-input');
    if (inputs.length === 1) {
      this.classList.add(classes['ua-form-item']);
      this.input = inputs[0];

      const errorId = `${this.input.id}-error`;
      const existingAriaDescribedby = this.input.getAttribute('aria-describedby');
      const ariaDescribedby = existingAriaDescribedby 
        ? `${existingAriaDescribedby} ${errorId}`
        : errorId;    
      this.input.setAttribute('aria-describedby', ariaDescribedby);

      this.#validationBlock = document.createElement('div');
      this.#validationBlock.classList.add(classes['validations']);
      this.#validationBlock.setAttribute('aria-live', 'polite');
      this.#validationBlock.setAttribute('aria-atomic', 'true');
      this.#validationBlock.setAttribute('id', `${this.input.id}-error`);
      this.appendChild(this.#validationBlock);
      this.setupEventListeners();
      if (this.input.attributes.required && !this.input.hasAttribute('data-skip-required-marker')) {
        this.addRequiredAnnotation();
      }
    } else throw new Error('Must attach exactly one input, textarea, or select.')
  }
}
